import OSS from 'ali-oss'
import { ref, shallowRef } from 'vue'
import request from '@/axios'
import utils, { isMobile } from './index'

// 使用 ref 存储单例
const ossClientInstance = shallowRef(null)
// 使用 ref 存储单例(知识库)
const ossClientInstanceKnow = shallowRef(null)
const ossDir = ref('')
const ossDirKnow = ref('')
const checkpoints = ref({})
// OSS 文件名默认使用时间戳。批量并发上传时，多个文件可能落在同一毫秒，
// 因此需要保证连续生成的时间戳不会重复，避免文件被上传到同一个对象路径。
let lastGeneratedFileTimestamp = 0

function getStsSignature(data) {
  return request({
    url: '/inside/ai/aliyun/oss/sts/getSignature',
    data
  })
}

// 创建 OSS 客户端单例
const createOssClient = async () => {
  // 如果已经存在实例，直接返回
  if (ossClientInstance.value) {
    return ossClientInstance.value
  }
  const res = await getStsSignature({})
  const { bucket, region, encryption, signatureToken, securityToken, dir, viewurl } = res.result
  ossDir.value = dir
  ossClientInstance.value = new OSS({
    endpoint: viewurl,
    cname: true,
    accessKeyId: encryption,
    accessKeySecret: signatureToken,
    stsToken: securityToken,
    bucket,
    secure: true,
    region,
    refreshSTSToken: async () => {
      const info = await getStsSignature({})
      return {
        accessKeyId: info.result.encryption,
        accessKeySecret: info.result.signatureToken,
        stsToken: info.result.securityToken
      }
    },
    refreshSTSTokenInterval: 1000 * 60 * 60
  })

  return ossClientInstance.value
}

// 创建 OSS 客户端单例(知识库)
const createOssClientKnow = async () => {
  // 如果已经存在实例，直接返回
  if (ossClientInstanceKnow.value) {
    return ossClientInstanceKnow.value
  }
  const res = await getStsSignature({})
  const { bucket, region, encryption, signatureToken, securityToken, dir, viewurl } = res.result
  ossDirKnow.value = dir
  ossClientInstanceKnow.value = new OSS({
    endpoint: viewurl, // 自定义域名
    cname: true, // 配合endpoint使用
    accessKeyId: encryption,
    accessKeySecret: signatureToken,
    stsToken: securityToken,
    bucket,
    secure: true,
    region,
    refreshSTSToken: async () => {
      const info = await getStsSignature({})
      return {
        accessKeyId: info.result.encryption,
        accessKeySecret: info.result.signatureToken,
        stsToken: info.result.securityToken
      }
    },
    refreshSTSTokenInterval: 1000 * 60 * 60
  })

  return ossClientInstanceKnow.value
}

// 封装成 hook
export function useOSS() {
  // 持久化上传方法，文件永远有效，不会被删除
  const persistentUpload = async (file, callback) => {
    return uploadFile(file, callback, false, true)
  }
  // 上传文件方法
  const uploadFile = async (
    file,
    callback,
    isBackup = false,
    isPersistent = false,
    isKnowledge = false
  ) => {
    let ossClient = null
    if (isKnowledge) {
      ossClient = await createOssClientKnow()
    } else {
      ossClient = await createOssClient()
    }
    const fileName = await generateFileName({
      file,
      isBackup,
      isPersistent
    })
    const result = await ossClient.multipartUpload(fileName, file, {
      progress: (progress, checkpoint) => {
        if (checkpoint) {
          checkpoints.value[checkpoint.uploadId] = checkpoint
        }
        const percent = Math.round(progress * 100)
        if (typeof callback === 'function') {
          callback(percent)
        }
      },
      checkpoint: checkpoints.value[file.breakUploadId]
    })

    const url = result.res.requestUrls[0]
    return url.split('?')[0]
  }

  // 初始化分片上传
  const initMultipartUpload = async (fileName) => {
    const client = await createOssClient()
    const result = await client.initMultipartUpload(fileName)
    return result.uploadId
  }

  // 上传分片
  const uploadPart = async (fileName, uploadId, partNumber, chunk, start, end, options) => {
    const client = await createOssClient()
    return await client.uploadPart(fileName, uploadId, partNumber, chunk, start, end, options)
  }

  // 完成分片上传
  const completeMultipartUpload = async (uploadId, fileName, uploadedParts) => {
    const client = await createOssClient()
    return await client.completeMultipartUpload(fileName, uploadId, uploadedParts)
  }

  // 直接下载文件（防止一些文件被浏览器直接打开）
  const downloadFile = async (filePath, fileName) => {
    try {
      const client = await createOssClient()
      // 从OSS获取文件内容
      const result = await client.get(filePath.replace(/^https?:\/\/[^/]+\//, ''))
      const contentType = result.res.headers['content-type'] || 'application/octet-stream'
      // 将内容转为Blob并生成下载链接
      const blob = new Blob([result.content], { type: contentType })
      const url = URL.createObjectURL(blob)
      // a标签下载
      const element = document.createElement('a')
      element.download = fileName
      element.href = url
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()
      setTimeout(() => {
        document.body.removeChild(element)
        URL.revokeObjectURL(url)
      }, 100)
    } catch (err) {
      throw new Error(`文件下载异常: ${err.message}`)
    }
  }

  const generateFileName = async ({ file, isBackup, isPersistent }) => {
    await createOssClient()
    const corpid = utils.cleanCorpIdPrefix('') //移除corpId中的特定前缀
    const userId = utils.xbbLocalStorageGet('userId') // 获取当前用户的userId
    const date = new Date()
    const now = date.getTime()
    // 如果使用并发上传文件，会导致最后拿到的文件名一致
    const timestamp = Math.max(now, lastGeneratedFileTimestamp + 1)
    lastGeneratedFileTimestamp = timestamp
    // 获取文件后缀
    let ext = ''
    if (typeof file === 'string') {
      ext = file.replace(/.+\./, '').toLowerCase() || 'mp3'
    } else {
      ext = file.name?.replace(/.+\./, '').toLowerCase() || 'mp3'
    }
    if (isBackup) {
      const platform = isMobile() ? 'dingtalk' : 'web'
      return `${ossDir.value}/${corpid}/${userId}/${platform}/temp/${timestamp}.${ext}`
    }
    if (isPersistent) {
      return `${ossDir.value}/${corpid}/${userId}/${timestamp}.${ext}`
    }
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    return `${ossDir.value}/${year}-${month}/${corpid}/${userId}/${timestamp}.${ext}`
  }

  // 删除文件
  const deleteFile = async (filePath) => {
    try {
      const client = await createOssClient()
      // 如果是完整URL，提取相对路径（object key）
      const objectKey = filePath.replace(/^https?:\/\/[^/]+\//, '')
      await client.delete(objectKey)
      return true
    } catch (err) {
      throw new Error(`文件删除异常: ${err.message}`)
    }
  }

  // 拷贝文件
  const copyFile = async (sourcePath, targetPath) => {
    try {
      const client = await createOssClient()
      // 如果是完整URL，提取相对路径（object key）
      const sourceKey = sourcePath.replace(/^https?:\/\/[^/]+\//, '')
      const targetKey = targetPath.replace(/^https?:\/\/[^/]+\//, '')
      const result = await client.copy(targetKey, sourceKey)
      // 返回拷贝后的文件URL（从result中获取或构建）
      return result.res.requestUrls?.[0]?.split('?')[0] || targetPath
    } catch (err) {
      throw new Error(`文件拷贝异常: ${err.message}`)
    }
  }

  // 重命名文件（复制+删除原文件）
  const renameFile = async (oldPath, newPath) => {
    try {
      // 先拷贝到新路径
      const newFilePath = await copyFile(oldPath, newPath)
      // 删除原文件放到异步流程中处理，不阻塞返回
      deleteFile(oldPath).catch((err) => {
        // 静默处理删除失败，不影响重命名操作的成功状态
        console.warn(`删除源文件失败: ${err.message}`, err)
      })
      return newFilePath
    } catch (err) {
      throw new Error(`文件重命名异常: ${err.message}`)
    }
  }

  // 追加上传方法（简化版，仅支持 Blob 格式）
  const appendUpload = async (fileName, content, expectedPosition, fileExists = false) => {
    try {
      const client = await createOssClient()

      // 1. 获取文件的实际长度（用于验证位置）
      let actualFileLength = 0

      // 如果知道文件不存在，跳过检查以避免404错误
      if (!fileExists) {
        actualFileLength = 0
        console.log(`[追加上传] 新文件，从位置0开始: ${fileName}`)
      } else {
        try {
          const metaResult = await client.getObjectMeta(fileName)
          const contentLength = metaResult.res.headers['content-length']
          actualFileLength = contentLength ? Number(contentLength) : 0
        } catch (err) {
          // 文件不存在，从位置0开始（这是正常情况，第一次上传时文件不存在）
          if (err.code === 'NoSuchKey' || err.status === 404) {
            actualFileLength = 0
            console.log(`[追加上传] 文件不存在，从位置0开始: ${fileName}`)
          } else {
            throw err
          }
        }
      }

      // 2. 验证位置是否匹配
      if (expectedPosition !== actualFileLength) {
        // 抛出错误，让调用方重试
        throw new Error(
          `Position mismatch: expected=${expectedPosition}, actual=${actualFileLength}`
        )
      }

      // 3. 执行追加上传
      const result = await client.append(fileName, content, {
        position: actualFileLength
      })

      // 4. 构建文件URL
      const url = result.res.requestUrls?.[0]?.split('?')[0] || fileName

      // 5. 计算下一个位置：始终使用 actualFileLength + content.size
      const nextPosition = actualFileLength + content.size

      return {
        nextAppendPosition: nextPosition,
        url
      }
    } catch (err) {
      console.error(`追加上传异常: ${err.message}`, err)
    }
  }

  return {
    client: ossClientInstance,
    uploadFile,
    downloadFile,
    deleteFile,
    copyFile,
    renameFile,
    appendUpload,
    initMultipartUpload,
    uploadPart,
    completeMultipartUpload,
    getStsSignature,
    generateFileName,
    persistentUpload
  }
}
